import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, RefreshCw, MapPin, X, ArrowUp, ArrowDown, Pencil, Film } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";

const inp = "bg-black/30 border border-white/15 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C77746]";
const btn = "inline-flex items-center gap-2 px-3 py-2 border border-white/20 text-[11px] uppercase tracking-widest hover:border-white/50 disabled:opacity-40";
const fmtDur = (s) => (s == null ? "" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);
const fmtMB = (b) => (b ? `${(b / 1024 / 1024).toFixed(1)} MB` : "");

async function uploadVideo(file, opts, onProgress) {
  const chunk = opts.chunk_bytes;
  const total = Math.ceil(file.size / chunk);
  const { data: init } = await api.post("/admin/videos/upload/init", {
    filename: file.name, size: file.size, content_type: file.type, total_chunks: total, replace_id: opts.replaceId || null,
  });
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("chunk", file.slice(i * chunk, (i + 1) * chunk), "chunk");
    await api.post(`/admin/videos/upload/${init.upload_id}/chunk/${i}`, fd, {
      onUploadProgress: (e) => onProgress(Math.min(0.99, (i * chunk + e.loaded) / file.size)),
    });
  }
  const { data } = await api.post(`/admin/videos/upload/${init.upload_id}/complete`);
  onProgress(1);
  return data;
}

function Uploader({ options, replaceId, onDone, compact }) {
  const [prog, setProg] = useState(null);
  const inputRef = useRef(null);
  const pick = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!options.allowed_ext.includes(ext)) { toast.error(`Unsupported format .${ext}. Use ${options.allowed_ext.map(e => e.toUpperCase()).join(", ")}.`); return; }
    if (file.size > options.max_bytes) { toast.error(`File is ${fmtMB(file.size)} — maximum is ${options.max_bytes / 1024 / 1024} MB.`); return; }
    setProg(0);
    try {
      await uploadVideo(file, { ...options, replaceId }, setProg);
      toast.success(replaceId ? "New file uploaded — re-processing video" : "Uploaded — compressing for the web…");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setProg(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const accept = options.allowed_ext.map(e => `.${e}`).join(",");
  if (compact) {
    return (
      <>
        <button type="button" className={btn} disabled={prog !== null} onClick={() => inputRef.current?.click()} data-testid={`replace-video-${replaceId}`}>
          <RefreshCw size={12} /> {prog === null ? "Replace" : `${Math.round(prog * 100)}%`}
        </button>
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={e => pick(e.target.files?.[0])} />
      </>
    );
  }
  return (
    <div
      onDragOver={e => e.preventDefault()}
      onDrop={e => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
      className="border border-dashed border-white/25 p-8 text-center hover:border-[#C77746]/70 transition-colors" data-testid="video-dropzone">
      <input ref={inputRef} type="file" accept={accept} className="hidden" data-testid="video-file-input" onChange={e => pick(e.target.files?.[0])} />
      {prog === null ? (
        <>
          <Upload className="mx-auto mb-3 text-white/50" size={22} />
          <p className="text-sm text-white/70">Drag a video here or <button type="button" onClick={() => inputRef.current?.click()} className="underline text-[#C77746]" data-testid="video-browse-btn">browse</button></p>
          <p className="text-xs text-white/40 mt-2">MP4 · MOV · WebM — up to {options.max_bytes / 1024 / 1024} MB. Compressed automatically for fast streaming.</p>
        </>
      ) : (
        <div data-testid="video-upload-progress">
          <div className="text-xs uppercase tracking-widest text-white/60 mb-2">{prog >= 1 ? "Finishing…" : `Uploading ${Math.round(prog * 100)}%`}</div>
          <div className="h-1.5 bg-white/10"><div className="h-full bg-[#C77746] transition-[width]" style={{ width: `${Math.round(prog * 100)}%` }} /></div>
        </div>
      )}
    </div>
  );
}

function VideoCard({ v, options, onChanged, onPlace }) {
  const [title, setTitle] = useState(v.title || "");
  useEffect(() => setTitle(v.title || ""), [v.title]);
  const saveTitle = async () => {
    if (title === v.title) return;
    await api.patch(`/admin/videos/${v.id}`, { title });
    toast.success("Title saved");
    onChanged();
  };
  const del = async () => {
    if (!window.confirm(`Delete "${v.title}" from your library?`)) return;
    try { await api.delete(`/admin/videos/${v.id}`); toast.success("Deleted"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  return (
    <div className="border border-white/10 bg-white/[0.03]" data-testid={`video-card-${v.id}`}>
      <div className="aspect-video bg-black/50 relative overflow-hidden">
        {v.status === "ready" ? (
          <img src={`${BACKEND_URL}${v.poster_url}`} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/50 text-xs uppercase tracking-widest">
            <Film size={20} className="mb-2" />
            {v.status === "processing" ? <span className="animate-pulse" data-testid={`video-status-${v.id}`}>Processing…</span> : <span className="text-red-400" data-testid={`video-status-${v.id}`}>Failed</span>}
          </div>
        )}
        {v.status === "ready" && <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 font-mono-brand">{fmtDur(v.duration)}</span>}
      </div>
      <div className="p-3 space-y-2">
        <input value={title} onChange={e => setTitle(e.target.value)} onBlur={saveTitle} onKeyDown={e => e.key === "Enter" && e.target.blur()}
          className={`${inp} w-full`} data-testid={`video-title-${v.id}`} />
        <div className="text-[11px] text-white/40 flex flex-wrap gap-x-3">
          {v.width && <span>{v.width}×{v.height}</span>}
          {v.size_bytes && <span>{fmtMB(v.size_bytes)}</span>}
          <span>{v.placements.length ? `On ${v.placements.length} page${v.placements.length > 1 ? "s" : ""}` : "Not placed"}</span>
        </div>
        {v.status === "error" && <div className="text-xs text-red-400" data-testid={`video-error-${v.id}`}>{v.error}</div>}
        <div className="flex flex-wrap gap-2 pt-1">
          <button type="button" className={`${btn} border-[#C77746]/60 text-[#C77746]`} disabled={v.status !== "ready"} onClick={() => onPlace(v)} data-testid={`place-video-${v.id}`}><MapPin size={12} /> Place</button>
          <Uploader options={options} replaceId={v.id} onDone={onChanged} compact />
          <button type="button" className={`${btn} hover:border-red-400 hover:text-red-400`} onClick={del} data-testid={`delete-video-${v.id}`}><Trash2 size={12} /></button>
        </div>
      </div>
    </div>
  );
}

function PlacementModal({ initial, videos, options, artworks, onClose, onSaved }) {
  const [f, setF] = useState({ video_id: "", page: "home", spot: "below_hero", layout: "block", title: "", caption: "", autoplay: false, artwork_id: "all", ...initial });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const spots = options.pages[f.page]?.spots || {};
  const isHero = f.page === "home" && f.spot === "hero_background";
  useEffect(() => { if (!spots[f.spot]) set("spot", Object.keys(spots)[0]); }, [f.page]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault();
    if (!f.video_id) { toast.error("Choose a video"); return; }
    const body = { ...f, artwork_id: f.page === "artwork" ? f.artwork_id : null, ...(isHero ? { layout: "banner", autoplay: true, title: "", caption: "" } : {}) };
    try {
      if (initial?.id) await api.patch(`/admin/video-placements/${initial.id}`, body);
      else await api.post("/admin/video-placements", body);
      toast.success(initial?.id ? "Placement updated" : "Video placed on page");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save"); }
  };
  const ready = videos.filter(v => v.status === "ready");
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="placement-modal">
      <form onSubmit={submit} className="bg-[#111] border border-white/10 w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif text-2xl">{initial?.id ? "Edit placement" : "Place a video"}</h2>
          <button type="button" onClick={onClose} data-testid="placement-close"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="text-xs text-white/60 sm:col-span-2">Video
            <select value={f.video_id} onChange={e => set("video_id", e.target.value)} className={`${inp} w-full mt-1`} data-testid="placement-video">
              <option value="">Choose…</option>
              {ready.map(v => <option key={v.id} value={v.id}>{v.title} ({fmtDur(v.duration)})</option>)}
            </select>
          </label>
          <label className="text-xs text-white/60">Page
            <select value={f.page} onChange={e => set("page", e.target.value)} className={`${inp} w-full mt-1`} data-testid="placement-page">
              {Object.entries(options.pages).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
            </select>
          </label>
          <label className="text-xs text-white/60">Where on the page
            <select value={f.spot} onChange={e => set("spot", e.target.value)} className={`${inp} w-full mt-1`} data-testid="placement-spot">
              {Object.entries(spots).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          {f.page === "artwork" && (
            <label className="text-xs text-white/60 sm:col-span-2">Which artwork page(s)
              <select value={f.artwork_id || "all"} onChange={e => set("artwork_id", e.target.value)} className={`${inp} w-full mt-1`} data-testid="placement-artwork">
                <option value="all">Every artwork page</option>
                <option value="all_prints">Every print page</option>
                {artworks.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
              </select>
            </label>
          )}
          {isHero ? (
            <p className="text-xs text-white/50 sm:col-span-2 border border-white/10 p-3" data-testid="placement-hero-note">
              Plays silently on loop behind the homepage headline, replacing the hero image (the image stays as the fallback while the video loads and on slow connections). Only one hero video at a time — saving replaces the current one.
            </p>
          ) : (
            <>
              <label className="text-xs text-white/60">Layout
                <select value={f.layout} onChange={e => set("layout", e.target.value)} className={`${inp} w-full mt-1`} data-testid="placement-layout">
                  {Object.entries(options.layouts).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label className="text-xs text-white/60 flex items-end gap-2 pb-2">
                <input type="checkbox" checked={!!f.autoplay} onChange={e => set("autoplay", e.target.checked)} data-testid="placement-autoplay" />
                Autoplay muted &amp; loop (viewers tap for sound)
              </label>
              <label className="text-xs text-white/60 sm:col-span-2">Title (optional)
                <input value={f.title || ""} onChange={e => set("title", e.target.value)} className={`${inp} w-full mt-1`} data-testid="placement-title" />
              </label>
              <label className="text-xs text-white/60 sm:col-span-2">Caption (optional)
                <textarea value={f.caption || ""} onChange={e => set("caption", e.target.value)} rows={2} className={`${inp} w-full mt-1`} data-testid="placement-caption" />
              </label>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className={btn}>Cancel</button>
          <button type="submit" className="px-5 py-2 bg-[#C77746] text-black text-xs uppercase tracking-widest" data-testid="placement-save">Save</button>
        </div>
      </form>
    </div>
  );
}

function PlacementRow({ p, first, last, onEdit, onRemove, onMove }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-white/10" data-testid={`placement-row-${p.id}`}>
      <div className="w-20 aspect-video bg-black/50 shrink-0 overflow-hidden">
        {p.video?.poster_url && <img src={`${BACKEND_URL}${p.video.poster_url}`} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{p.video?.title || "(video missing)"}{p.title ? <span className="text-white/50"> — {p.title}</span> : null}</div>
        <div className="text-[11px] text-white/40">{p.spot_label}{p.spot === "hero_background" ? "" : ` · ${p.layout_label}${p.autoplay ? " · autoplay muted" : " · click to play"}`}{p.page === "artwork" ? ` · ${p.artwork_id === "all" ? "every artwork" : p.artwork_id === "all_prints" ? "every print" : "one artwork"}` : ""}</div>
      </div>
      <div className="flex gap-1">
        <button type="button" className={btn} disabled={first} onClick={() => onMove(-1)} aria-label="Move up"><ArrowUp size={12} /></button>
        <button type="button" className={btn} disabled={last} onClick={() => onMove(1)} aria-label="Move down"><ArrowDown size={12} /></button>
        <button type="button" className={btn} onClick={onEdit} data-testid={`edit-placement-${p.id}`}><Pencil size={12} /></button>
        <button type="button" className={`${btn} hover:border-red-400 hover:text-red-400`} onClick={onRemove} data-testid={`remove-placement-${p.id}`}><X size={12} /> Remove from page</button>
      </div>
    </div>
  );
}

export function VideosAdmin() {
  const [options, setOptions] = useState(null);
  const [videos, setVideos] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [modal, setModal] = useState(null);
  const load = () => {
    api.get("/admin/videos").then(r => setVideos(r.data));
    api.get("/admin/video-placements").then(r => setPlacements(r.data));
  };
  useEffect(() => {
    api.get("/videos/options").then(r => setOptions(r.data));
    api.get("/artworks").then(r => setArtworks(r.data)).catch(() => {});
    load();
  }, []);
  const processing = videos.some(v => v.status === "processing");
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [processing]);

  const removePlacement = async (p) => {
    if (!window.confirm("Remove this video from the page? It stays in your library.")) return;
    await api.delete(`/admin/video-placements/${p.id}`);
    toast.success("Removed from page");
    load();
  };
  const move = async (group, idx, dir) => {
    const ids = group.map(p => p.id);
    const j = idx + dir;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await api.post("/admin/video-placements/reorder", { ids });
    load();
  };

  if (!options) return null;
  const grouped = Object.keys(options.pages).map(page => ({ page, label: options.pages[page].label, items: placements.filter(p => p.page === page) })).filter(g => g.items.length);

  return (
    <div data-testid="videos-admin">
      <div className="mb-8">
        <h1 className="font-serif text-4xl mb-1">Videos</h1>
        <div className="overline text-white/40">Upload once, place anywhere — home, gallery, business, product pages</div>
      </div>
      <Uploader options={options} onDone={load} />

      <h2 className="font-serif text-2xl mt-12 mb-4">Library <span className="text-white/40 text-base">({videos.length})</span></h2>
      {videos.length === 0 ? <p className="text-white/40 text-sm">No videos yet. Upload your first clip above.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="video-library">
          {videos.map(v => <VideoCard key={v.id} v={v} options={options} onChanged={load} onPlace={(vid) => setModal({ video_id: vid.id })} />)}
        </div>
      )}

      <div className="flex items-center justify-between mt-14 mb-4">
        <h2 className="font-serif text-2xl">Where videos appear</h2>
        <button type="button" onClick={() => setModal({})} className="px-4 py-2 bg-[#C77746] text-black text-xs uppercase tracking-widest" data-testid="new-placement-btn"><MapPin size={12} className="inline mr-1" /> Place a video</button>
      </div>
      {grouped.length === 0 ? <p className="text-white/40 text-sm">No videos are placed on any page yet.</p> : grouped.map(g => (
        <div key={g.page} className="mb-8" data-testid={`placements-page-${g.page}`}>
          <div className="overline text-[#C77746] mb-1">{g.label}</div>
          {g.items.map((p, i) => (
            <PlacementRow key={p.id} p={p} first={i === 0} last={i === g.items.length - 1}
              onEdit={() => setModal(p)} onRemove={() => removePlacement(p)} onMove={(d) => move(g.items, i, d)} />
          ))}
        </div>
      ))}

      {modal && <PlacementModal initial={modal} videos={videos} options={options} artworks={artworks} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}
